import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Form,
  Input,
  InputNumber,
  Button,
  Switch,
  Select,
  Space,
  message,
  Spin,
  Alert,
  Typography,
  Divider,
  Row,
  Col,
  Tooltip,
  Upload,
  ColorPicker
} from 'antd';
import {
  AimOutlined,
  SettingOutlined,
  CreditCardOutlined,
  MailOutlined,
  GlobalOutlined,
  ExperimentOutlined,
  CarOutlined,
  SaveOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SendOutlined,
  ApiOutlined,
  LockOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  HomeOutlined,
  UploadOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import {
  adminService,
  HOMEPAGE_IMAGE_ACCEPT,
  validateHomepageImage,
  type SystemSettings,
} from '../../services/adminService';
import ShippingSettings from '../../components/admin/ShippingSettings';
import HeroBackgroundEditor, {
  formatFocusPoint,
  parseFocusPoint,
  parseZoom,
} from '../../components/admin/HeroBackgroundEditor';
import {
  DEFAULT_HOMEPAGE_CONTENT,
  heroPhotoLayerStyle,
  photoWebUrl,
} from '../../services/homepageService';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

/**
 * A homepage background colour as a Form field (issue #140). The value is a
 * '#RRGGBB' string, or '' for the colour the site ships with — clearing the
 * picker is how staff go back to it.
 */
const ColorField: React.FC<{ value?: string; onChange?: (value: string) => void }> = ({
  value,
  onChange,
}) => (
  <Space>
    <ColorPicker
      value={value || undefined}
      disabledAlpha
      allowClear
      showText={(color) => (value ? color.toHexString().toUpperCase() : 'Default')}
      onChange={(color) => onChange?.(color.toHexString().slice(0, 7).toUpperCase())}
      onClear={() => onChange?.('')}
    />
    {value && (
      <Button size="small" onClick={() => onChange?.('')}>
        Use default
      </Button>
    )}
  </Space>
);


const Settings: React.FC = () => {
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingRazorpay, setTestingRazorpay] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [razorpayResult, setRazorpayResult] = useState<{ success: boolean; message: string } | null>(null);
  const [emailResult, setEmailResult] = useState<{ success: boolean; message: string } | null>(null);
  const [activeTab, setActiveTab] = useState('razorpay');
  const [testEmailAddress, setTestEmailAddress] = useState('');

  const [razorpayForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [generalForm] = Form.useForm();
  const [featuresForm] = Form.useForm();
  const [termsForm] = Form.useForm();
  const [topupTermsForm] = Form.useForm();
  const [reportsForm] = Form.useForm();
  const [homepageForm] = Form.useForm();
  // Path of the hero image as stored, so the preview updates on upload/reset
  // without reloading every setting on the page.
  const [heroImage, setHeroImage] = useState('');
  const [uploadingHero, setUploadingHero] = useState(false);
  // Photo behind the hero (issue #140), tracked the same way.
  const [heroBgImage, setHeroBgImage] = useState('');
  const [heroBgPosition, setHeroBgPosition] = useState('50% 50%');
  // How far it is zoomed in, in percent (issue #162).
  const [heroBgZoom, setHeroBgZoom] = useState(DEFAULT_HOMEPAGE_CONTENT.heroBgZoom);
  // The background photo open in the preview editor (issue #154): a newly
  // chosen file not yet uploaded, or the live photo being re-positioned.
  const [bgEditor, setBgEditor] = useState<{ url: string; file?: File } | null>(null);
  const [uploadingHeroBg, setUploadingHeroBg] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await adminService.getSystemSettings();
      setSettings(data);

      // Populate forms
      if (data.razorpay) razorpayForm.setFieldsValue(data.razorpay);
      emailForm.setFieldsValue(data.email);
      generalForm.setFieldsValue(data.general);
      featuresForm.setFieldsValue({
        allow_guest_checkout: data.features.allow_guest_checkout === 'true',
        require_email_verification: data.features.require_email_verification === 'true',
        maintenance_mode: data.features.maintenance_mode === 'true'
      });
      termsForm.setFieldsValue({
        text: data.terms?.text ?? ''
      });
      topupTermsForm.setFieldsValue({
        topup_text: data.terms?.topup_text ?? ''
      });
      homepageForm.setFieldsValue({
        hero_headline: data.homepage?.hero_headline ?? '',
        hero_subtext: data.homepage?.hero_subtext ?? '',
        promo_enabled: data.homepage?.promo_enabled === 'true',
        promo_text: data.homepage?.promo_text ?? '',
        hero_bg_color: data.homepage?.hero_bg_color ?? ''
      });
      setHeroImage(data.homepage?.hero_image ?? '');
      setHeroBgImage(data.homepage?.hero_bg_image ?? '');
      setHeroBgPosition(formatFocusPoint(parseFocusPoint(data.homepage?.hero_bg_position)));
      setHeroBgZoom(parseZoom(data.homepage?.hero_bg_zoom));

      const rawHour = parseInt(data.reports?.inactive_report_hour ?? '', 10);
      reportsForm.setFieldsValue({
        inactive_report_enabled: (data.reports?.inactive_report_enabled ?? 'true') !== 'false',
        inactive_report_day: parseInt(data.reports?.inactive_report_day ?? '1', 10) || 1,
        inactive_report_hour: Number.isFinite(rawHour) ? rawHour : 8
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
      message.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRazorpay = async (values: any) => {
    setSaving(true);
    try {
      await adminService.updateSettings({ razorpay: values });
      message.success('Razorpay settings saved successfully');
      setRazorpayResult(null);
      loadSettings();
    } catch (error) {
      console.error('Failed to save Razorpay settings:', error);
      message.error('Failed to save Razorpay settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmail = async (values: any) => {
    setSaving(true);
    try {
      await adminService.updateSettings({ email: values });
      message.success('Email settings saved successfully');
      setEmailResult(null);
      loadSettings();
    } catch (error) {
      console.error('Failed to save email settings:', error);
      message.error('Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Homepage content (issue #132). Settings are stored as strings, so the
   * banner's on/off switch is written as 'true'/'false' like the other flags.
   */
  const handleSaveHomepage = async (values: any) => {
    setSaving(true);
    try {
      await adminService.updateSettings({
        homepage: {
          hero_headline: values.hero_headline ?? '',
          hero_subtext: values.hero_subtext ?? '',
          promo_enabled: values.promo_enabled ? 'true' : 'false',
          promo_text: values.promo_text ?? '',
          hero_bg_color: values.hero_bg_color ?? ''
        }
      });
      message.success('Homepage content saved — customers see it on their next visit');
      loadSettings();
    } catch (error) {
      console.error('Failed to save homepage content:', error);
      message.error('Failed to save homepage content');
    } finally {
      setSaving(false);
    }
  };

  const handleHeroUpload = async (file: File) => {
    const invalidReason = validateHomepageImage(file);
    if (invalidReason) {
      message.error(invalidReason);
      return false;
    }
    setUploadingHero(true);
    try {
      const result = await adminService.uploadHomepageImage(file);
      setHeroImage(result.path);
      message.success('Homepage image updated');
    } catch (error: any) {
      // The backend says why (unreadable file, disk full); repeating it is the
      // difference between the admin retrying and the admin guessing.
      message.error(error?.message || 'Failed to upload the homepage image');
    } finally {
      setUploadingHero(false);
    }
    // Returning false keeps antd's Upload from trying to post the file itself.
    return false;
  };

  const handleHeroReset = async () => {
    try {
      await adminService.resetHomepageImage();
      setHeroImage('');
      message.success('Homepage image reset to the default');
    } catch (error) {
      console.error('Failed to reset homepage image:', error);
      message.error('Failed to reset the homepage image');
    }
  };

  // A chosen photo opens in the preview editor first; it is only uploaded, and
  // only goes live, when staff press Save there (issue #154).
  const handleHeroBgUpload = (file: File) => {
    const invalidReason = validateHomepageImage(file);
    if (invalidReason) {
      message.error(invalidReason);
      return false;
    }
    setBgEditor({ url: URL.createObjectURL(file), file });
    return false;
  };

  const handleHeroBgAdjust = () => {
    setBgEditor({ url: photoWebUrl(heroBgImage) });
  };

  const closeBgEditor = () => {
    if (bgEditor?.file) URL.revokeObjectURL(bgEditor.url);
    setBgEditor(null);
  };

  const handleBgEditorSave = async (position: string, zoom: number) => {
    if (!bgEditor) return;
    setUploadingHeroBg(true);
    try {
      if (bgEditor.file) {
        const result = await adminService.uploadHomepageBackground(bgEditor.file, position, zoom);
        setHeroBgImage(result.path);
        setHeroBgPosition(result.position ?? position);
        setHeroBgZoom(result.zoom ?? zoom);
        message.success('Background photo updated');
      } else {
        await adminService.updateHomepageBackgroundFraming(position, zoom);
        setHeroBgPosition(position);
        setHeroBgZoom(zoom);
        message.success('Background position saved');
      }
      closeBgEditor();
    } catch (error: any) {
      // Keep the editor open so the chosen framing isn't lost on a retry.
      message.error(error?.message || 'Failed to save the background photo');
    } finally {
      setUploadingHeroBg(false);
    }
  };

  const handleHeroBgReset = async () => {
    try {
      await adminService.resetHomepageBackground();
      setHeroBgImage('');
      message.success('Background photo removed');
    } catch (error) {
      console.error('Failed to remove homepage background photo:', error);
      message.error('Failed to remove the background photo');
    }
  };

  const handleSaveGeneral = async (values: any) => {
    setSaving(true);
    try {
      await adminService.updateSettings({ general: values });
      message.success('General settings saved successfully');
      loadSettings();
    } catch (error) {
      console.error('Failed to save general settings:', error);
      message.error('Failed to save general settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTerms = async (values: { text: string }) => {
    setSaving(true);
    try {
      await adminService.updateSettings({ terms: { text: values.text ?? '' } });
      message.success('Checkout Terms & Conditions saved');
      loadSettings();
    } catch (error) {
      console.error('Failed to save Terms & Conditions:', error);
      message.error('Failed to save Terms & Conditions');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTopupTerms = async (values: { topup_text: string }) => {
    setSaving(true);
    try {
      await adminService.updateSettings({ terms: { topup_text: values.topup_text ?? '' } });
      message.success('Wallet Top-Up Terms & Conditions saved');
      loadSettings();
    } catch (error) {
      console.error('Failed to save wallet Terms & Conditions:', error);
      message.error('Failed to save wallet Terms & Conditions');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveReports = async (values: any) => {
    setSaving(true);
    try {
      await adminService.updateSettings({
        reports: {
          inactive_report_enabled: values.inactive_report_enabled ? 'true' : 'false',
          inactive_report_day: String(values.inactive_report_day ?? 1),
          inactive_report_hour: String(values.inactive_report_hour ?? 8)
        }
      });
      message.success('Report schedule saved');
      loadSettings();
    } catch (error) {
      console.error('Failed to save report schedule:', error);
      message.error('Failed to save report schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFeatures = async (values: any) => {
    setSaving(true);
    try {
      await adminService.updateSettings({
        features: {
          allow_guest_checkout: values.allow_guest_checkout ? 'true' : 'false',
          require_email_verification: values.require_email_verification ? 'true' : 'false',
          maintenance_mode: values.maintenance_mode ? 'true' : 'false'
        }
      });
      message.success('Feature settings saved successfully');
      loadSettings();
    } catch (error) {
      console.error('Failed to save feature settings:', error);
      message.error('Failed to save feature settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestRazorpay = async () => {
    setTestingRazorpay(true);
    setRazorpayResult(null);
    try {
      const result = await adminService.testRazorpayConnection();
      setRazorpayResult(result);
      if (result.success) {
        message.success(result.message);
      } else {
        message.error(result.message);
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Failed to test Razorpay connection';
      setRazorpayResult({ success: false, message: errorMsg });
      message.error(errorMsg);
    } finally {
      setTestingRazorpay(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    setEmailResult(null);
    try {
      const result = await adminService.testEmailConnection(testEmailAddress || undefined);
      setEmailResult(result);
      if (result.success) {
        message.success(result.message);
      } else {
        message.error(result.message);
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Failed to test email connection';
      setEmailResult({ success: false, message: errorMsg });
      message.error(errorMsg);
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" tip="Loading settings..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ marginBottom: '8px' }}>
          <SettingOutlined style={{ marginRight: '12px' }} />
          System Settings
        </Title>
        <Text type="secondary">
          Configure API keys, email settings, and system preferences
        </Text>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} type="card">
        {/* Razorpay Settings — active gateway for checkout + wallet top-up */}
        <TabPane
          tab={
            <span>
              <CreditCardOutlined />
              Razorpay
            </span>
          }
          key="razorpay"
        >
          <Card>
            <Form
              form={razorpayForm}
              layout="vertical"
              onFinish={handleSaveRazorpay}
              initialValues={settings?.razorpay}
            >
              <Alert
                message="Razorpay API Configuration"
                description="Enter your Razorpay API keys to enable payment processing. Find these in your Razorpay Dashboard under Settings > API Keys. Set the Webhook Secret to match the secret configured on your Razorpay webhook pointing at /api/v1/payments/webhook."
                type="info"
                showIcon
                style={{ marginBottom: '24px' }}
              />

              <Form.Item
                name="key_id"
                label={
                  <Space>
                    <ApiOutlined />
                    <span>Key ID</span>
                    <Tooltip title="Starts with 'rzp_test_' or 'rzp_live_'. Public — sent to the browser.">
                      <InfoCircleOutlined style={{ color: '#999' }} />
                    </Tooltip>
                  </Space>
                }
                rules={[
                  { pattern: /^rzp_(live|test)_/, message: 'Must start with rzp_live_ or rzp_test_' }
                ]}
              >
                <Input
                  placeholder="rzp_test_xxxxxxxxxxxxxxxx"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="key_secret"
                label={
                  <Space>
                    <LockOutlined />
                    <span>Key Secret</span>
                    <Tooltip title="Server-side secret. Keep this secure!">
                      <InfoCircleOutlined style={{ color: '#999' }} />
                    </Tooltip>
                  </Space>
                }
              >
                <Input.Password
                  placeholder="Razorpay Key Secret"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="razorpay_webhook_secret"
                label={
                  <Space>
                    <LockOutlined />
                    <span>Webhook Secret</span>
                    <Tooltip title="Used to verify webhook HMAC signatures.">
                      <InfoCircleOutlined style={{ color: '#999' }} />
                    </Tooltip>
                  </Space>
                }
              >
                <Input.Password
                  placeholder="Razorpay webhook signing secret"
                  size="large"
                />
              </Form.Item>

              {razorpayResult && (
                <Alert
                  message={razorpayResult.success ? 'Connection Successful' : 'Connection Failed'}
                  description={razorpayResult.message}
                  type={razorpayResult.success ? 'success' : 'error'}
                  showIcon
                  icon={razorpayResult.success ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                  style={{ marginBottom: '16px' }}
                />
              )}

              <Divider />

              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={saving}
                  size="large"
                >
                  Save Razorpay Settings
                </Button>
                <Button
                  icon={<ApiOutlined />}
                  onClick={handleTestRazorpay}
                  loading={testingRazorpay}
                  size="large"
                >
                  Test Connection
                </Button>
              </Space>
            </Form>
          </Card>
        </TabPane>

        {/* Email Settings */}
        <TabPane
          tab={
            <span>
              <MailOutlined />
              Email / SMTP
            </span>
          }
          key="email"
        >
          <Card>
            <Form
              form={emailForm}
              layout="vertical"
              onFinish={handleSaveEmail}
              initialValues={settings?.email}
            >
              <Alert
                message="Email/SMTP Configuration"
                description="Configure your SMTP server settings to enable transactional emails (order confirmations, notifications, etc.)."
                type="info"
                showIcon
                style={{ marginBottom: '24px' }}
              />

              <Row gutter={16}>
                <Col xs={24} sm={16}>
                  <Form.Item
                    name="smtp_host"
                    label="SMTP Host"
                    rules={[{ required: true, message: 'Please enter SMTP host' }]}
                  >
                    <Input placeholder="smtp.example.com" size="large" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="smtp_port"
                    label="SMTP Port"
                    rules={[{ required: true, message: 'Please enter SMTP port' }]}
                  >
                    <Input placeholder="587" size="large" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="smtp_user"
                    label="SMTP Username"
                    rules={[{ required: true, message: 'Please enter SMTP username' }]}
                  >
                    <Input placeholder="user@example.com" size="large" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="smtp_password"
                    label="SMTP Password"
                    rules={[{ required: true, message: 'Please enter SMTP password' }]}
                  >
                    <Input.Password placeholder="Enter SMTP password" size="large" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left">Sender Information</Divider>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="from_email"
                    label="From Email"
                    rules={[
                      { type: 'email', message: 'Please enter a valid email' }
                    ]}
                  >
                    <Input placeholder="noreply@example.com" size="large" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="from_name"
                    label="From Name"
                  >
                    <Input placeholder="DasoonEgg" size="large" />
                  </Form.Item>
                </Col>
              </Row>

              {emailResult && (
                <Alert
                  message={emailResult.success ? 'Connection Successful' : 'Connection Failed'}
                  description={emailResult.message}
                  type={emailResult.success ? 'success' : 'error'}
                  showIcon
                  icon={emailResult.success ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                  style={{ marginBottom: '16px' }}
                />
              )}

              <Divider />

              <Space direction="vertical" style={{ width: '100%' }}>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={saving}
                    size="large"
                  >
                    Save Email Settings
                  </Button>
                </Space>

                <Divider orientation="left" plain>Test Email</Divider>

                <Space>
                  <Input
                    placeholder="Enter email address for test"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    style={{ width: '300px' }}
                    size="large"
                  />
                  <Button
                    icon={<SendOutlined />}
                    onClick={handleTestEmail}
                    loading={testingEmail}
                    size="large"
                  >
                    {testEmailAddress ? 'Send Test Email' : 'Test Connection'}
                  </Button>
                </Space>
              </Space>
            </Form>
          </Card>
        </TabPane>

        {/* General Settings */}
        <TabPane
          tab={
            <span>
              <GlobalOutlined />
              General
            </span>
          }
          key="general"
        >
          <Card>
            <Form
              form={generalForm}
              layout="vertical"
              onFinish={handleSaveGeneral}
              initialValues={settings?.general}
            >
              <Alert
                message="General Settings"
                description="Configure basic site settings like name, currency, and order minimums."
                type="info"
                showIcon
                style={{ marginBottom: '24px' }}
              />

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="site_name"
                    label="Site Name"
                  >
                    <Input placeholder="DasoonEgg" size="large" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="admin_email"
                    label="Admin Email"
                    rules={[{ type: 'email', message: 'Please enter a valid email' }]}
                  >
                    <Input placeholder="admin@example.com" size="large" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24}>
                  <Form.Item
                    name="order_notification_recipients"
                    label="Order Notification Recipients"
                    extra="Comma-separated emails notified on every new order (e.g. processing team). The Admin Email above is always notified too."
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value || !value.trim()) return Promise.resolve();
                          const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                          const invalid = value
                            .split(',')
                            .map((s: string) => s.trim())
                            .filter((s: string) => s.length > 0)
                            .filter((s: string) => !emailRe.test(s));
                          return invalid.length
                            ? Promise.reject(new Error(`Invalid email(s): ${invalid.join(', ')}`))
                            : Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder="processing@dasoon.com, ops@dasoon.com"
                      size="large"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left">Pricing & Tax</Divider>

              <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="currency"
                    label="Currency"
                  >
                    <Select placeholder="Select currency" size="large">
                      <Select.Option value="SGD">SGD - Singapore Dollar</Select.Option>
                      <Select.Option value="USD">USD - US Dollar</Select.Option>
                      <Select.Option value="MYR">MYR - Malaysian Ringgit</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                {/* GST Rate field removed — prices are GST-inclusive; GST is not
                    charged separately, so the rate is not used anywhere. */}
                <Col xs={12} sm={12} md={8}>
                  <Form.Item
                    name="delivery_charge"
                    label="Delivery Charge (below minimum)"
                    extra="Flat fee added at checkout when the cart subtotal is below the Minimum Order Amount."
                  >
                    <Input placeholder="10" size="large" addonBefore="$" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="min_order_amount"
                    label="Minimum Order Amount"
                    extra="Orders at or above this amount ship free; below this amount the Delivery Charge applies."
                  >
                    <Input placeholder="50" size="large" addonBefore="$" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
                size="large"
              >
                Save General Settings
              </Button>
            </Form>
          </Card>
        </TabPane>

        {/* Shipping */}
        <TabPane
          tab={
            <span>
              <CarOutlined />
              Shipping
            </span>
          }
          key="shipping"
        >
          <ShippingSettings />
        </TabPane>

        {/* Homepage content (issue #132) */}
        <TabPane
          tab={
            <span>
              <HomeOutlined />
              Homepage
            </span>
          }
          key="homepage"
        >
          <Card style={{ marginBottom: 24 }}>
            <Form form={homepageForm} layout="vertical" onFinish={handleSaveHomepage}>
              <Alert
                message="Homepage content"
                description="The headline, background and promotional banner on the home page. Any field left blank keeps the wording the site ships with. Changes are live as soon as they are saved — no deployment needed."
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />

              <Form.Item
                name="hero_headline"
                label="Headline"
                extra="Leave blank to keep the wording the site ships with."
              >
                <Input maxLength={120} showCount placeholder="Importer and Wholesaler of all egg products" />
              </Form.Item>

              <Form.Item
                name="hero_subtext"
                label="Paragraph under the headline"
                extra="Leave blank to keep the wording the site ships with."
              >
                <Input.TextArea rows={4} maxLength={500} showCount />
              </Form.Item>

              <Divider />

              <Form.Item
                name="promo_enabled"
                label="Show the promotional banner"
                valuePropName="checked"
                extra="A strip across the top of the home page, under the hero — for deals, discounts and announcements."
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="promo_text"
                label="Promotional banner text"
                extra="The banner stays hidden while this is empty, even when the switch above is on."
              >
                <Input.TextArea rows={2} maxLength={200} showCount placeholder="10% off all cartons this week" />
              </Form.Item>

              <Divider orientation="left">Top section background</Divider>

              <Form.Item
                name="hero_bg_color"
                label="Background colour"
                extra="Behind the headline and the hero image. Leave on Default for the green gradient. A background photo, if uploaded below, is shown over this colour."
              >
                <ColorField />
              </Form.Item>

              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving} size="large">
                Save Homepage Content
              </Button>
            </Form>
          </Card>

          <Card title="Hero image">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <img
                src={heroImage ? `/api/v1/${heroImage}` : '/images/dasoon-hero-egg.png'}
                alt="Homepage hero"
                style={{ maxWidth: '100%', maxHeight: 240, objectFit: 'contain' }}
              />
              <Text type="secondary">
                {heroImage
                  ? 'Your uploaded image. Landscape artwork works best; it is resized to fit within 1200px.'
                  : 'The image the site ships with. Upload one to replace it.'}
              </Text>
              <Space wrap>
                <Upload accept={HOMEPAGE_IMAGE_ACCEPT} showUploadList={false} beforeUpload={handleHeroUpload}>
                  <Button icon={<UploadOutlined />} loading={uploadingHero}>
                    Upload new image
                  </Button>
                </Upload>
                {heroImage && (
                  <Button icon={<DeleteOutlined />} onClick={handleHeroReset}>
                    Use the default image
                  </Button>
                )}
              </Space>
            </Space>
          </Card>

          <Card title="Top section background photo" style={{ marginTop: 24 }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {heroBgImage ? (
                <div
                  role="img"
                  aria-label="Homepage background photo"
                  style={{
                    position: 'relative',
                    overflow: 'hidden',
                    height: 180,
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    padding: 24,
                    color: '#fff',
                    fontSize: 20,
                    fontWeight: 700
                  }}
                >
                  {/* Same dark, framed layer as the homepage, so staff judge
                      the photo as customers will see it behind white text. */}
                  <div aria-hidden style={heroPhotoLayerStyle(photoWebUrl(heroBgImage), heroBgPosition, heroBgZoom)} />
                  <span style={{ position: 'relative' }}>Headline text preview</span>
                </div>
              ) : null}
              <Text type="secondary">
                {heroBgImage
                  ? 'Shown across the whole top section, slightly darkened so the white headline stays readable.'
                  : 'No photo: the top section uses its background colour. A wide landscape photo works best; it is resized to fit within 1920px.'}
              </Text>
              <Space wrap>
                <Upload accept={HOMEPAGE_IMAGE_ACCEPT} showUploadList={false} beforeUpload={handleHeroBgUpload}>
                  <Button icon={<UploadOutlined />} loading={uploadingHeroBg}>
                    {heroBgImage ? 'Replace photo' : 'Upload background photo'}
                  </Button>
                </Upload>
                {heroBgImage && (
                  <Button icon={<AimOutlined />} onClick={handleHeroBgAdjust}>
                    Adjust position
                  </Button>
                )}
                {heroBgImage && (
                  <Button icon={<DeleteOutlined />} onClick={handleHeroBgReset}>
                    Remove photo
                  </Button>
                )}
              </Space>
            </Space>
          </Card>

          <HeroBackgroundEditor
            open={!!bgEditor}
            imageUrl={bgEditor?.url ?? ''}
            initialPosition={bgEditor?.file ? '50% 50%' : heroBgPosition}
            initialZoom={bgEditor?.file ? DEFAULT_HOMEPAGE_CONTENT.heroBgZoom : heroBgZoom}
            headline={homepageForm.getFieldValue('hero_headline') || DEFAULT_HOMEPAGE_CONTENT.heroHeadline}
            subtext={homepageForm.getFieldValue('hero_subtext') || DEFAULT_HOMEPAGE_CONTENT.heroSubtext}
            saving={uploadingHeroBg}
            onCancel={closeBgEditor}
            onSave={handleBgEditorSave}
          />
        </TabPane>

        {/* Terms & Conditions */}
        <TabPane
          tab={
            <span>
              <FileTextOutlined />
              Terms & Conditions
            </span>
          }
          key="terms"
        >
          <Card style={{ marginBottom: 24 }}>
            <Form form={termsForm} layout="vertical" onFinish={handleSaveTerms}>
              <Alert
                message="Checkout Terms & Conditions"
                description="Shown to customers on the checkout page (Review Order step) when they click the 'Terms and Conditions' link. Each line appears as a separate paragraph."
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />

              <Form.Item
                name="text"
                label="Checkout terms text"
                extra="Plain text, one statement per line. Blank lines are ignored."
                rules={[{ required: true, message: 'Please enter Terms & Conditions text' }]}
              >
                <Input.TextArea rows={10} maxLength={4000} showCount placeholder="Line 1...\nLine 2...\nLine 3..." />
              </Form.Item>

              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving} size="large">
                Save Checkout Terms
              </Button>
            </Form>
          </Card>

          <Card>
            <Form form={topupTermsForm} layout="vertical" onFinish={handleSaveTopupTerms}>
              <Alert
                message="Wallet Top-Up Terms & Conditions"
                description="Shown to customers on the Wallet Top-Up page when they click the 'Terms and Conditions' link. Edited independently of the checkout terms."
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />

              <Form.Item
                name="topup_text"
                label="Wallet top-up terms text"
                extra="Plain text, one statement per line. Blank lines are ignored."
                rules={[{ required: true, message: 'Please enter wallet Terms & Conditions text' }]}
              >
                <Input.TextArea rows={10} maxLength={4000} showCount placeholder="Line 1...\nLine 2...\nLine 3..." />
              </Form.Item>

              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving} size="large">
                Save Wallet Top-Up Terms
              </Button>
            </Form>
          </Card>
        </TabPane>

        {/* Reports — monthly inactive-customer email schedule */}
        <TabPane
          tab={
            <span>
              <ClockCircleOutlined />
              Reports
            </span>
          }
          key="reports"
        >
          <Card>
            <Form
              form={reportsForm}
              layout="vertical"
              onFinish={handleSaveReports}
            >
              <Alert
                message="Monthly Inactive-Customer Report"
                description="Automatically emails the inactive-customer report (30/60/90+/never summary + list) to the Admin Email and Order Notification Recipients configured under General. Times use the server timezone (Asia/Singapore)."
                type="info"
                showIcon
                style={{ marginBottom: '24px' }}
              />

              <Form.Item
                name="inactive_report_enabled"
                label="Send automatically each month"
                valuePropName="checked"
                extra="When off, no monthly email is sent (you can still use “Send Now” on the Inactive Customers tab)."
              >
                <Switch checkedChildren="On" unCheckedChildren="Off" />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={12} sm={8}>
                  <Form.Item
                    name="inactive_report_day"
                    label="Day of month"
                    extra="1–28 (capped at 28 so it always exists)."
                    rules={[{ required: true, message: 'Enter a day' }]}
                  >
                    <InputNumber min={1} max={28} style={{ width: '100%' }} size="large" />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8}>
                  <Form.Item
                    name="inactive_report_hour"
                    label="Time of day"
                    rules={[{ required: true, message: 'Select a time' }]}
                  >
                    <Select
                      size="large"
                      options={Array.from({ length: 24 }, (_, h) => ({
                        value: h,
                        label: `${String(h).padStart(2, '0')}:00`
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
                size="large"
              >
                Save Report Schedule
              </Button>
            </Form>
          </Card>
        </TabPane>

        {/* Feature Flags */}
        <TabPane
          tab={
            <span>
              <ExperimentOutlined />
              Features
            </span>
          }
          key="features"
        >
          <Card>
            <Form
              form={featuresForm}
              layout="vertical"
              onFinish={handleSaveFeatures}
            >
              <Alert
                message="Feature Flags"
                description="Enable or disable various features across the platform."
                type="info"
                showIcon
                style={{ marginBottom: '24px' }}
              />

              <Form.Item
                name="allow_guest_checkout"
                label="Allow Guest Checkout"
                valuePropName="checked"
                extra="Allow customers to checkout without creating an account"
              >
                <Switch checkedChildren="On" unCheckedChildren="Off" />
              </Form.Item>

              <Divider />

              <Form.Item
                name="require_email_verification"
                label="Require Email Verification"
                valuePropName="checked"
                extra="Require new users to verify their email before accessing the platform"
              >
                <Switch checkedChildren="On" unCheckedChildren="Off" />
              </Form.Item>

              <Divider />

              <Form.Item
                name="maintenance_mode"
                label="Maintenance Mode"
                valuePropName="checked"
                extra="Enable maintenance mode to prevent access to the site"
              >
                <Switch
                  checkedChildren="On"
                  unCheckedChildren="Off"
                  style={{ backgroundColor: featuresForm.getFieldValue('maintenance_mode') ? '#ff4d4f' : undefined }}
                />
              </Form.Item>

              <Alert
                message="Warning"
                description="Enabling maintenance mode will prevent all non-admin users from accessing the site."
                type="warning"
                showIcon
                style={{ marginBottom: '24px' }}
              />

              <Divider />

              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
                size="large"
              >
                Save Feature Settings
              </Button>
            </Form>
          </Card>
        </TabPane>
      </Tabs>

      <div style={{ marginTop: '24px', textAlign: 'right' }}>
        <Button
          icon={<ReloadOutlined />}
          onClick={loadSettings}
          disabled={loading}
        >
          Refresh All Settings
        </Button>
      </div>
    </div>
  );
};

export default Settings;
